import React from 'react';
import { Box, Typography, Chip, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Calendar, Clock } from 'lucide-react';

interface ProposalHeaderProps {
  title: string;
  date: string;
  timeline: string;
  timelineType: string;
  status: 'submitted' | 'approved' | 'in-review' | 'rejected' | 'draft';
  description: string;
  isEditable?: boolean;
  onInputChange?: (field: string, value: string) => void;
  onSave?: (field: string) => void;
  editedFields?: {
    title: string;
    description: string;
    timeline: string;
  };
}

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'submitted':
        return {
          bg: 'rgba(0, 122, 255, 0.1)',
          color: '#00a3ff',
        };
      case 'approved':
        return {
          bg: 'rgba(0, 204, 153, 0.1)',
          color: '#00CC99',
        };
      case 'in-review':
        return {
          bg: 'rgba(255, 204, 0, 0.1)',
          color: '#ffcc00',
        };
      case 'rejected':
        return {
          bg: 'rgba(255, 59, 48, 0.1)',
          color: '#ff3b30',
        };
      case 'draft':
        return {
          bg: 'rgba(200, 200, 200, 0.1)',
          color: 'rgba(255, 255, 255, 0.7)',
        };
      default:
        return {
          bg: 'rgba(200, 200, 200, 0.1)',
          color: 'rgba(255, 255, 255, 0.7)',
        };
    }
  };

  const statusColor = getStatusColor();

  return {
    backgroundColor: statusColor.bg,
    color: statusColor.color,
    borderRadius: theme.spacing(1),
    fontWeight: 'bold',
    textTransform: 'capitalize',
  };
});

const ProposalHeader: React.FC<ProposalHeaderProps> = ({
  title,
  date,
  timeline,
  timelineType,
  status,
  description,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          {title}
        </Typography>
        <StatusChip label={status} status={status} />
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item display="flex" alignItems="center">
          <Calendar size={16} style={{ marginRight: 8, opacity: 0.7 }} />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {date}
          </Typography>
        </Grid>
        <Grid item display="flex" alignItems="center">
          <Clock size={16} style={{ marginRight: 8, opacity: 0.7 }} />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {timeline}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Timeline Type: {timelineType}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
        {description}
      </Typography>
    </Box>
  );
};

export default ProposalHeader; 